import type { EnvironmentResource, GetScopedProjectTemplateValuesForProjectBffResponse, ProjectResource, GetConnectedEnvironmentsForTenantedProjectBffResponse, GetScopedCommonTemplatesWithValuesBffResponse, } from "@octopusdeploy/octopus-server-client";
import React, { useState } from "react";
import type { ProjectTenantVariablesFilterState } from "~/areas/projects/components/Variables/TenantVariables/FiltersBar";
import type { TemplatesWarnings } from "~/areas/projects/components/Variables/TenantVariables/ProjectTenantVariablesNavigationTabs";
import { useScopedProjectTemplatesFilterState } from "~/areas/projects/components/Variables/TenantVariables/ScopedTenantVariables/useScopedProjectTemplatesFilterState";
import { usePageState } from "~/areas/tenants/components/Paging/usePageState";
import { repository } from "~/clientInstance";
import { type DoBusyTask, useDoBusyTaskEffect } from "~/components/DataBaseComponent/index";
import RequestRaceConditioner from "~/utils/RequestRaceConditioner/index";
interface Actions {
    setPageState: (newPageNumber: number, newPageSize: number) => void;
    setFilter: (newFilter: ProjectTenantVariablesFilterState) => void;
    resetFilter: (newFilter: Partial<ProjectTenantVariablesFilterState>) => void;
    onSave: () => Promise<void>;
}
export interface ScopedProjectTemplatesState {
    data: GetScopedProjectTemplateValuesForProjectBffResponse | null;
    connectedEnvironments: Map<string, EnvironmentResource[]>;
    warnings: TemplatesWarnings;
    pageSize: number;
    filterState: ProjectTenantVariablesFilterState;
    actions: Actions;
}
const pagingSelectionKey = "octoProjectTenantVariablesPagingSelection";
export function useScopedProjectTemplatesState(project: ProjectResource, doBusyTask: DoBusyTask): ScopedProjectTemplatesState {
    const [data, setData] = useState<GetScopedProjectTemplateValuesForProjectBffResponse | null>(null);
    const [connectedEnvironments, setConnectedEnvironments] = useState<Map<string, EnvironmentResource[]>>(new Map());
    const [warnings, setWarnings] = useState<TemplatesWarnings>({ projectTemplates: false, commonTemplates: false });
    const [pageState, setPageState] = usePageState(pagingSelectionKey);
    const [filterState, setFilterState] = useScopedProjectTemplatesFilterState();
    const [isInitialPageLoad, setIsInitialPageLoad] = useState<boolean>(true);
    const raceConditionerRef: Readonly<React.MutableRefObject<RequestRaceConditioner>> = React.useRef(new RequestRaceConditioner());
    const actions: Actions = {
        setPageState: (newPageNumber, newPageSize) => {
            setFilterState({ ...filterState, pageNumber: newPageNumber });
            setPageState(filterState.pageNumber, newPageSize);
        },
        setFilter: (newFilter) => setFilterState({ ...newFilter, pageNumber: 1 }),
        resetFilter: (newFilter) => setFilterState({ ...defaultFilter, ...newFilter }),
        onSave: async () => {
            await doBusyTask(async () => {
                await raceConditionerRef.current.avoidStaleResponsesForRequest(repository.Projects.getScopedProjectTemplatesWithValues(project.Id, (filterState.pageNumber - 1) * pageState.pageSize, pageState.pageSize, filterState), (response) => {
                    setData(response);
                    setWarnings({
                        projectTemplates: response?.AnyMissingValues ?? false,
                        commonTemplates: warnings.commonTemplates,
                    });
                });
            });
        },
    };
    useDoBusyTaskEffect(doBusyTask, async () => {
        const loadProjectTemplates = () => repository.Projects.getScopedProjectTemplatesWithValues(project.Id, (filterState.pageNumber - 1) * pageState.pageSize, pageState.pageSize, filterState);
        const getConnectedEnvironments = () => repository.Projects.getConnectedEnvironments(project.Id);
        const updateDataState = (projectResponse: GetScopedProjectTemplateValuesForProjectBffResponse | null, commonResponse: GetScopedCommonTemplatesWithValuesBffResponse | null, connectedEnvironmentsResponse: GetConnectedEnvironmentsForTenantedProjectBffResponse | null) => {
            setData(projectResponse);
            if (connectedEnvironmentsResponse) {
                const environmentsMap = new Map<string, EnvironmentResource[]>();
                connectedEnvironmentsResponse?.ConnectedEnvironments.forEach((connectedEnvironment) => {
                    environmentsMap.set(connectedEnvironment.TenantId, connectedEnvironment.Environments);
                });
                setConnectedEnvironments(environmentsMap);
            }
            setWarnings((previous) => ({ projectTemplates: projectResponse?.AnyMissingValues ?? previous.projectTemplates, commonTemplates: commonResponse?.AnyMissingValues ?? previous.commonTemplates }));
        };
        if (isInitialPageLoad) {
            const [projectResponse, commonResponse, connectedEnvironmentsResponse] = await Promise.all([
                loadProjectTemplates(),
                repository.Projects.getScopedCommonTemplatesWithValues(project.Id, (filterState.pageNumber - 1) * pageState.pageSize, pageState.pageSize, filterState),
                getConnectedEnvironments(),
            ]);
            updateDataState(projectResponse, commonResponse, connectedEnvironmentsResponse);
            setIsInitialPageLoad(false);
        }
        else {
            await raceConditionerRef.current.avoidStaleResponsesForRequest(loadProjectTemplates(), (response) => updateDataState(response, null, null));
        }
    }, [project, filterState, pageState.pageSize]);
    return { data, connectedEnvironments, warnings, pageSize: pageState.pageSize, filterState, actions };
}
const defaultFilter: ProjectTenantVariablesFilterState = {
    filterByName: undefined,
    filterByEnvironments: [],
    filterByTenants: [],
    filterByTags: [],
    filterByValueStatus: undefined,
    filterByTemplateId: undefined,
    filterByLibraryVariableSetIds: [],
    filterByProjectIds: [],
    pageNumber: 1,
};
